import { useT } from '@kmgestao/i18n';
import { Badge, PageContainer, PageIntroCard, PageSectionCard, Skeleton } from '@kmgestao/ui';

import { useCurrentClinicId } from '../../../features/auth/index.js';
import { useClinicProfile } from '../../../features/clinic-profile/index.js';
import { ClinicShell } from '../ClinicShell.js';

import { ClinicProfileForm } from './ClinicProfileForm.js';

/**
 * Página `/perfil` — perfil cadastral da clínica do owner/admin.
 *
 * Acesso: `clinic_owner` / `clinic_admin` (via `<RequireClinicAdmin>`).
 * Rota fica **fora** do `RequireClinicSubscriptionActive` (gate de
 * pagamento) — owner em `payment-pending` ainda precisa conseguir
 * revisar/atualizar dados cadastrais (ex.: corrigir email pra receber
 * a fatura).
 *
 * Conteúdo:
 *   - `PageIntroCard` com eyebrow "Configurações", título e badge
 *     ilustrativa de "Dados públicos"
 *   - `PageSectionCard` único contendo `<ClinicProfileForm>` com 3
 *     seções internas (Identificação, Contato, Endereço)
 *
 * **Padrão canônico** das páginas internas (`PageContainer` +
 * `PageIntroCard` + `PageSectionCard`) — ver `docs/design-system.md`
 * §"Padrão para páginas internas".
 *
 * Logo + cor do tema NÃO entram aqui — ficam reservados pra PR-M2-04
 * (identidade visual + paletas curadas em light+dark).
 */
export function ClinicProfilePage() {
  const { t } = useT('clinics');
  const clinicId = useCurrentClinicId();
  const profileQ = useClinicProfile(clinicId);

  return (
    <ClinicShell>
      <PageContainer>
        <PageIntroCard
          eyebrow={t('profile.eyebrow')}
          title={t('profile.title')}
          description={t('profile.description')}
          meta={<Badge variant="outline">{t('profile.sections.identity.title')}</Badge>}
        />

        {!clinicId ? (
          <PageSectionCard
            title={t('profile.noClinic.title')}
            description={t('profile.noClinic.description')}
          >
            <div />
          </PageSectionCard>
        ) : profileQ.isLoading ? (
          <PageSectionCard title={t('profile.loading')} description="">
            <ProfileSkeleton />
          </PageSectionCard>
        ) : profileQ.isError ? (
          <PageSectionCard title={t('profile.title')} description="">
            <p className="text-sm text-destructive" role="alert">
              {t('profile.loadError', {
                message: profileQ.error instanceof Error ? profileQ.error.message : '',
              })}
            </p>
          </PageSectionCard>
        ) : profileQ.data ? (
          <PageSectionCard
            title={t('profile.title')}
            description={t('profile.description')}
            contentClassName="space-y-6"
          >
            <ClinicProfileForm clinicId={clinicId} data={profileQ.data} />
          </PageSectionCard>
        ) : (
          <PageSectionCard
            title={t('profile.noClinic.title')}
            description={t('profile.noClinic.description')}
          >
            <div />
          </PageSectionCard>
        )}
      </PageContainer>
    </ClinicShell>
  );
}

function ProfileSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-11 w-full" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-11 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-11 w-full" />
        </div>
      </div>
      <Skeleton className="h-11 w-40" />
    </div>
  );
}
